import React, { useState, useMemo, useEffect } from 'react';
import { Plus } from 'lucide-react';
import Button from '../../components/ui/Button';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import AccountsFilters from './components/AccountsFilters';
import AccountsTable from './components/AccountsTable';
import AccountDrawer from './components/AccountDrawer';
import ComponentIdBadge from '../../components/ui/ComponentIdBadge';
import SampleDataToggle from '../../components/ui/SampleDataToggle';
import { sampleDataService } from '../../services/sampleDataService';
import { accountService } from '../../services/accountService';
import { useAuth } from '../../contexts/AuthContext';
import { useRealtimeEntity } from '../../hooks/useRealtimeEntity';
import { supabase } from '../../lib/supabase';

export default function AccountsPage() {
  const { profile } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [sizeFilter, setSizeFilter] = useState('');
  const [showSampleData, setShowSampleData] = useState(false);
  const [sampleOrganizations, setSampleOrganizations] = useState([]);
  const [filters, setFilters] = useState({
    industry: '',
    revenue: '',
    region: ''
  });
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const transformOrganization = (org) => ({
    id: org?.id,
    company: org?.name,
    industry: org?.industry,
    revenue: org?.revenue,
    contactCount: org?.contact_count || 0,
    dealValue: org?.deal_value,
    lastActivity: org?.last_activity,
    owner: org?.owner,
    region: org?.region
  });

  // loadAccounts function declaration
  const loadAccounts = async () => {
    if (!profile?.tenant_id) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase?.from('tenants')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;
      
      const transformedData = data?.map(transformOrganization) || [];
      setAccounts(transformedData);
    } catch (error) {
      console.error('Error loading accounts:', error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSampleDataToggle = async (isActive) => {
    try {
      setShowSampleData(isActive);
      if (isActive) {
        const sampleData = await sampleDataService?.getSampleDataByEntity('organizations', profile?.tenant_id);
        setSampleOrganizations(sampleData);
      } else {
        setSampleOrganizations([]);
      }
    } catch (err) {
      console.error('Error loading sample data:', err);
    }
  };

  // Real-time accounts/organizations synchronization
  const transformAccount = (dbAccount) => ({
    id: dbAccount?.id,
    name: dbAccount?.name,
    subdomain: dbAccount?.subdomain,
    industryId: dbAccount?.industry_id,
    subscriptionTier: dbAccount?.subscription_tier,
    subscriptionStatus: dbAccount?.subscription_status,
    logoUrl: dbAccount?.logo_url,
    isActive: dbAccount?.is_active,
    createdAt: dbAccount?.created_at,
    updatedAt: dbAccount?.updated_at
  });

  const { data: realtimeAccounts, setData: setRealtimeAccounts, isConnected } = useRealtimeEntity(
    'tenants',
    accounts,
    transformAccount
  );

  // Sync real-time data with local state
  useEffect(() => {
    if (isConnected) {
      setAccounts(realtimeAccounts);
    }
  }, [realtimeAccounts, isConnected]);

  useEffect(() => {
    loadAccounts();
  }, [profile?.tenant_id]);

  // Filter accounts based on active filters
  const filteredAccounts = useMemo(() => {
    // Combine real and sample data if sample data is active
    const allAccounts = showSampleData ? [...accounts, ...sampleOrganizations] : accounts;
    
    return allAccounts?.filter(account => {
      if (filters?.industry && account?.industry?.toLowerCase() !== filters?.industry) {
        return false;
      }
      
      if (filters?.revenue) {
        const revenue = parseFloat(account?.revenue?.replace(/[$,]/g, ''));
        switch (filters?.revenue) {
          case '0-1M':
            if (revenue >= 1000000) return false;
            break;
          case '1M-10M':
            if (revenue < 1000000 || revenue >= 10000000) return false;
            break;
          case '10M-50M':
            if (revenue < 10000000 || revenue >= 50000000) return false;
            break;
          case '50M-100M':
            if (revenue < 50000000 || revenue >= 100000000) return false;
            break;
          case '100M+':
            if (revenue < 100000000) return false;
            break;
        }
      }
      
      if (filters?.region && account?.region !== filters?.region) {
        return false;
      }
      
      return true;
    });
  }, [accounts, filters, showSampleData, sampleOrganizations]);

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleSidebarClose = () => {
    setIsSidebarOpen(false);
  };

  const handleRowClick = (account) => {
    setSelectedAccount(account);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setSelectedAccount(null);
  };

  const handleBulkAction = (action, selectedIds) => {
    console.log(`Bulk ${action} for accounts:`, selectedIds);
    // Implement bulk actions here
  };

  const handleFiltersChange = (newFilters) => {
    setFilters(newFilters);
  };

  const handleAddAccount = () => {
    setSelectedAccount(null);
    setIsDrawerOpen(true);
  };

  const handleViewAccount = (account) => {
    setSelectedAccount(account);
    setIsDrawerOpen(true);
  };

  const handleEditAccount = (account) => {
    setSelectedAccount(account);
    setIsDrawerOpen(true);
  };

  const handleDeleteAccount = (accountId) => {
    console.log('Delete account:', accountId);
    // Implement delete logic here
  };

  const handleSaveAccount = async (accountData) => {
    try {
      if (selectedAccount?.id) {
        await accountService?.update(selectedAccount?.id, accountData);
      } else {
        await accountService?.create(accountData);
      }
      await loadAccounts();
      setIsDrawerOpen(false);
    } catch (error) {
      console.error('Error saving account:', error);
      alert('Failed to save account: ' + error?.message);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <ComponentIdBadge 
        id="pg-accounts-main" 
        name="Accounts Main Page"
        description="Account management page with list view and filters"
        position="top-right"
      />
      <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header onToggleSidebar={handleSidebarToggle} />
        
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6 relative">
              <div>
                <ComponentIdBadge
                  id="hdr-accounts-title"
                  name="Accounts Page Header"
                  description="Page title and account statistics"
                  position="inline"
                  showLabel={true}
                />
                <h1 className="text-3xl font-bold text-gray-900">Accounts</h1>
                <p className="text-gray-600">Manage company accounts and relationships</p>
              </div>
              <div className="flex items-center gap-3 relative">
                <SampleDataToggle
                  entityType="organizations"
                  onToggle={handleSampleDataToggle}
                  isActive={showSampleData}
                />
                <ComponentIdBadge
                  id="btn-account-add"
                  name="Add New Account Button"
                  description="Opens drawer to create new company account"
                />
                <Button
                  onClick={handleAddAccount}
                  className="flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Account
                </Button>
              </div>
            </div>

            <div className="relative bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
              <ComponentIdBadge 
                id="cmp-accounts-filters" 
                name="Account Filters Component"
                description="Search and filter controls for accounts"
              />
              <AccountsFilters
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                industryFilter={industryFilter}
                setIndustryFilter={setIndustryFilter}
                sizeFilter={sizeFilter}
                setSizeFilter={setSizeFilter}
                onFiltersChange={handleFiltersChange}
                activeFilters={filters}
                resultCount={filteredAccounts?.length}
              />
            </div>

            <div className="relative bg-white rounded-lg shadow-sm border border-gray-200">
              <ComponentIdBadge 
                id="tbl-accounts-main" 
                name="Accounts Data Table"
                description="Main table displaying all account records with details"
              />
              <AccountsTable
                accounts={filteredAccounts}
                onViewAccount={handleViewAccount}
                onEditAccount={handleEditAccount}
                onDeleteAccount={handleDeleteAccount}
                onRowClick={handleRowClick}
                onBulkAction={handleBulkAction}
              />
            </div>
          </div>
        </main>
      </div>
      {isDrawerOpen && (
        <div className="relative">
          <ComponentIdBadge 
            id="drw-account-edit" 
            name="Account Details Drawer"
            description="Side panel for viewing/editing account information"
            position="top-right"
          />
          <AccountDrawer
            account={selectedAccount}
            isOpen={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            onSave={handleSaveAccount}
          />
        </div>
      )}
    </div>
  );
}